<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';
import { getHelpUrlForFeature } from 'dashboard/helper/featureHelper';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  logo: {
    type: String,
    default: '',
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);

const helpURL = computed(
  () => getHelpUrlForFeature(props.id) || getHelpUrlForFeature('integrations')
);

const actionLabel = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.CONFIGURE')
    : t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')
);

const actionIcon = computed(() =>
  props.enabled ? 'i-lucide-settings' : 'i-lucide-plug'
);

const logoFile = computed(() => props.logo || `${props.id}.png`);
const darkLogoFile = computed(() =>
  logoFile.value.replace('.png', '-dark.png')
);
</script>

<template>
  <div
    class="flex flex-row items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-5 py-3"
  >
    <div class="flex min-w-0 flex-1 items-center gap-4">
      <div class="flex shrink-0 size-12">
        <img
          :src="`/dashboard/images/integrations/${logoFile}`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${darkLogoFile}`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <div class="min-w-0 flex-1">
        <p
          class="mb-0 truncate text-base font-semibold leading-snug text-n-slate-12"
        >
          {{ name }}
        </p>
        <p class="mb-0 truncate text-sm text-n-slate-11">
          {{ replaceInstallationName(description) }}
        </p>
      </div>
    </div>
    <div
      class="flex shrink-0 flex-wrap items-center justify-end gap-3 sm:gap-4"
    >
      <a
        v-if="helpURL"
        :href="helpURL"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-11 hover:underline"
      >
        {{ $t('INTEGRATION_SETTINGS.LEARN_MORE') }}
      </a>
      <router-link :to="actionURL">
        <Button
          outline
          slate
          sm
          :icon="actionIcon"
          :label="actionLabel"
          class="rounded-xl !font-bold"
        />
      </router-link>
    </div>
  </div>
</template>
